import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Action, AlertProps, FlagProps, Parameter, showNotification} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isEmpty, isNil} from 'ramda';
import {concatAll, isFalse, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetReceiveNoteFastTrackQuery,
  useGetReceiveNoteQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';
import {isReceiveNoteFastTracksPending} from './utils/isReceiveNoteFastTracksPending';

export function ReceiveNoteDetail() {
  const navigate = useNavigate();

  const {receiveNoteId} = useWarehouseParams();

  const dateTimeFormatter = useDateTimeFormatter();

  const isFastTrackFeatureFlagEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE
  );

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [canCompleteFastTrack] = usePermissions({
    permissionKeys: ['fastTrackComplete'],
  });

  const {
    data: receiveNote,
    isLoading: isReceiveNoteLoading,
    isFetching: isReceiveNoteFetching,
    isError: hasReceiveNoteError,
  } = useGetReceiveNoteQuery({receiveNoteId}, {skip: isNilOrEmpty(receiveNoteId)});

  const {data: createdByUser, isLoading: isCreatedByUserLoading} = useGetUserQuery(
    {id: receiveNote?.createdBy ?? ''},
    {skip: isNilOrEmpty(receiveNote?.createdBy)}
  );

  const {data: updatedByUser, isLoading: isUpdatedByUserLoading} = useGetUserQuery(
    {id: receiveNote?.updatedBy ?? ''},
    {skip: isNilOrEmpty(receiveNote?.updatedBy)}
  );

  const {
    data: receiveNoteAction,
    isFetching: isReceiveNoteActionFetching,
    isError: hasReceiveNoteActionError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: receiveNoteId, resource: 'receive-note'},
    {skip: isNilOrEmpty(receiveNoteId)}
  );

  const {
    data: receiveNoteFastTracks,
    isLoading: isReceiveNoteFastTracksLoading,
    isError: isReceiveNoteFastTracksError,
  } = useGetReceiveNoteFastTrackQuery(
    {
      receiveNoteId,
    },
    {skip: isNilOrEmpty(receiveNoteId) || isFalse(isFastTrackFeatureFlagEnabled)}
  );

  const isPending = isReceiveNoteFastTracksPending(receiveNoteFastTracks);

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-receipt-note', contextId: receiveNoteId},
    {skip: !receiveNoteId}
  );

  const [putReceiveNoteAction, {isLoading: isReceiveNoteActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const handleDiscard = () => {
    navigate(warehouseRoutes.receiveNoteList);
  };

  const handleActionClick = (actionKey: string) => {
    const navigatePaths: Record<string, string | undefined> = {
      corrective: composePath(warehouseRoutes.receiveNoteCorrectionCreate, {
        params: {receiveNoteCreateId: receiveNoteId},
        isSearchParamsPreserved: false,
      }),
      complete: warehouseRoutes.receiveNoteList,
      delete: warehouseRoutes.receiveNoteList,
    };

    const handleSuccess = () => {
      const path = navigatePaths[actionKey];
      if (path) {
        navigate(path);
      }
    };

    if (actionKey === 'corrective') {
      handleSuccess();
      return;
    }

    if (actionKey === 'complete' && isFastTrackFeatureFlagEnabled) {
      const hasFastTrackIssues = isNotNilOrEmpty(receiveNoteFastTracks?.receiveNoteItems);

      if (hasFastTrackIssues && !canCompleteFastTrack) {
        return showNotification.warning(
          'entity.warehouse.labels.missingFastTrackCompletePermission'
        );
      }

      if (isPending) {
        return showNotification.warning('entity.warehouse.labels.receiveNotePleaseAcceptAllItems');
      }
    }

    if (actionKey === 'prntReceiveNote') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: receiveNoteId,
          originEntityPrintoutType: 'prntReceiveNote',
        },
      })
        .unwrap()
        .then((data) => {
          const pdfUrl = data?.printout?.pdfUrl;

          if (pdfUrl) {
            printPDF(pdfUrl);
          } else {
            showNotification.error();
          }
        })
        .then(() => documentCountQuery.refetch())
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.receiveNoteDetailDocuments, {
              params: {id: receiveNoteId},
            })
          );
        })
        .catch(handleApiError);
    }

    putReceiveNoteAction({
      id: receiveNoteId,
      resource: 'receive-note',
      body: {actionKey},
    })
      .unwrap()
      .then(() => handleSuccess())
      .then(() => documentCountQuery.refetch())
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: receiveNoteAction?.actions,
    callback: handleActionClick,
    isLoading: isReceiveNoteActionLoading || isPostPrintoutOnDemandLoading,
  });

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const isLoading =
    isReceiveNoteLoading ||
    isCreatedByUserLoading ||
    isReceiveNoteActionFetching ||
    isReceiveNoteFastTracksLoading ||
    isUpdatedByUserLoading;

  const hasError = hasReceiveNoteError || hasReceiveNoteActionError || isReceiveNoteFastTracksError;

  const isDraftReceiveNote = isNil(receiveNote);
  const isCompletedReceiveNote = receiveNote?.state === 'COMPLETED';

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser) ?? EMPTY_PLACEHOLDER}`;
  const createdAt = `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(receiveNote?.createdAt)}`;

  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUser) ?? EMPTY_PLACEHOLDER}`;
  const updatedAt = `${i18n.t('general.labels.updatedAt')}: ${formatToShortDate(receiveNote?.updatedAt ?? undefined)}`;

  const completedBy = `${i18n.t('general.labels.completedBy')}: ${isCompletedReceiveNote ? getUserName(updatedByUser) : EMPTY_PLACEHOLDER}`;
  const completedAt = `${i18n.t('general.labels.completedAt')}: ${isCompletedReceiveNote ? formatToShortDate(receiveNote?.updatedAt ?? undefined) : EMPTY_PLACEHOLDER}`;

  const headerTitle = isEmpty(receiveNoteId)
    ? i18n.t('entity.warehouse.labels.newReceiveNote')
    : `${i18n.t('entity.warehouse.labels.receiveNote')} ${defaultTo('', receiveNote?.receiveNoteNumber)}`;

  const headerParameters = match(isDraftReceiveNote)
    .returnType<Parameter[]>()
    .with(true, always([]))
    .otherwise(always([createdBy, createdAt, updatedBy, updatedAt, completedBy, completedAt]));

  const markersFlags = createHeaderFlags(receiveNote?.markers);

  const staticFlags = match(receiveNote?.state)
    .returnType<FlagProps[]>()
    .with('PENDING', () => [{colorScheme: 'blue', label: i18n.t('general.labels.pending')}])
    .with('COMPLETED', () => [{colorScheme: 'green', label: i18n.t('general.labels.completed')}])
    .otherwise(() => [{colorScheme: 'neutral', label: i18n.t('entity.warehouse.labels.draft')}]);

  const headerFlags = concatAll<FlagProps[]>([staticFlags, markersFlags]);

  const staticActions = buildArray<Action>().when(isDraftReceiveNote, {
    type: 'button',
    onClick: handleDiscard,
    title: i18n.t('general.actions.discard'),
    variant: 'secondary',
    'data-testid': testIds.warehouse.receiveNoteDetailOverview('headerActions.discard'),
  });

  const alerts = buildArray<AlertProps>()
    .whenNot(canEditReceiveNote, {
      type: 'banner',
      variant: 'info',
      message: i18n.t('module.warehouse.label.missingReceiveNoteEditPermission'),
    })
    .when(isPending && isFastTrackFeatureFlagEnabled, {
      type: 'banner',
      variant: 'warning',
      message: i18n.t('module.warehouse.label.receiveNoteFastTrackIssues'),
    });

  const header: DetailTemplateHeader = {
    title: headerTitle,
    isEntityLoading: isReceiveNoteFetching,
    icon: 'action/deployed',
    parameters: headerParameters,
    recordId: receiveNote?.receiveNoteId,
    resourceId: EntityResourceIds.receiveNote,
    flags: headerFlags,
    actions: [...headerActions, ...staticActions],
    controls: ['ASSIGNEE'],
    alerts,
  };

  const receiveNoteDetailHref = isNotNilOrEmpty(receiveNoteId)
    ? composePath(warehouseRoutes.receiveNoteDetailOverview, {
        params: {id: receiveNoteId},
      })
    : warehouseRoutes.receiveNoteCreate;

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: receiveNoteId || '',
    resourceId: 'RECEIVE_NOTE',
  });

  const navigation = buildArray<NavigationItem>([])
    .add({
      id: 'receiveNoteDetail',
      label: i18n.t('page.warehouse.labels.overview'),
      href: receiveNoteDetailHref,
      content: (
        <Overview
          receiveNote={receiveNote}
          isReceiveNoteLoading={isReceiveNoteLoading}
          hasReceiveNoteError={hasReceiveNoteError}
          receiveNoteFastTracks={receiveNoteFastTracks}
          data-testid={testIds.warehouse.receiveNoteDetailOverview('sections.overview')}
        />
      ),
      'data-testid': testIds.warehouse.receiveNoteDetailOverview('navigation.overview'),
    })
    .when(isNotNilOrEmpty(receiveNoteId), {
      id: 'receiveNoteDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.receiveNoteDetailDocuments, {
        params: {id: receiveNoteId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.receiveNoteDetailDocuments('sections.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.receiveNoteDetailDocuments('navigation.documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'receiveNoteChecklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.receiveNoteDetailChecklists, {
        params: {id: receiveNoteId},
      }),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="warehouse-receipt-note"
            contextId={receiveNoteId}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.receiveNoteDetailChecklists('checklists'),
    });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.receiveNote')} />
      <DetailTemplate
        key={generateHashFromObjects(receiveNote)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isReceiveNoteActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.receiveNoteDetailOverview('page')}
      />
    </>
  );
}
